import { useMutation } from '@apollo/client'
import { useTheme } from '@emotion/react'
import {
    Box,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { GetCandidateProfileForRecruiterQuery } from '__generated__/graphql'
import {
    Dispatch,
    FC,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { UpdateCandidateTestScores } from 'recruiter/graphql/candidateProfile.gql'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem, isValidGreScore, isValidSatScore } from 'shared/utils'
import { EDUCATION_SECTIONS } from '.'
import EditButtonsContainer from './EditButtonsContainer'
import EducationSectionTitleContainer from './EducationSectionTitleContainer'

interface TestScoresProps {
    testScores?:
        | NonNullable<
              GetCandidateProfileForRecruiterQuery['candidate']
          >['testScores']
        | null
    edit: EDUCATION_SECTIONS | null
    setEdit: Dispatch<SetStateAction<EDUCATION_SECTIONS | null>>
    candidateId: string
    closeAfterSave: () => void
    setUnsavedChangesExist: Dispatch<SetStateAction<boolean>>
    setEditWithUnsavedChanges: (newEdit: EDUCATION_SECTIONS | null) => void
}

const TestScores: FC<TestScoresProps> = ({
    edit,
    setEdit,
    testScores,
    candidateId,
    closeAfterSave,
    setUnsavedChangesExist,
    setEditWithUnsavedChanges,
}) => {
    const toast = useToast()
    const [formValues, setFormValues] = useState({
        satMathScore: testScores?.satMathScore || null,
        satVerbalScore: testScores?.satVerbalScore || null,
        satWritingScore: testScores?.satWritingScore || null,
        satTotal: testScores?.satTotal || null,
        actScore: testScores?.actScore || null,
        gmatScore: testScores?.gmatScore || null,
        greTotalScore: testScores?.greTotalScore || null,
        greVerbalScore: testScores?.greVerbalScore || null,
        greQuantitativeScore: testScores?.greQuantitativeScore || null,
        greWritingScore: testScores?.greWritingScore || null,
        tookActSatTests: testScores?.tookActSatTests ?? null,
        remembersTakingActSatTests:
            testScores?.remembersTakingActSatTests ?? null,
    })
    const [errorValues, setErrorValues] = useState({
        satMathScore: '',
        satVerbalScore: '',
        satWritingScore: '',
        satTotal: '',
        actScore: '',
        gmatScore: '',
        greTotalScore: '',
        greVerbalScore: '',
        greQuantitativeScore: '',
        greWritingScore: '',
        tookActSatTests: '',
        remembersTakingActSatTests: '',
    })
    const theme = useTheme()

    useEffect(() => {
        if (edit !== EDUCATION_SECTIONS.TEST_SCORES)
            setFormValues({
                satMathScore: testScores?.satMathScore || null,
                satVerbalScore: testScores?.satVerbalScore || null,
                satWritingScore: testScores?.satWritingScore || null,
                satTotal: testScores?.satTotal || null,
                actScore: testScores?.actScore || null,
                gmatScore: testScores?.gmatScore || null,
                greTotalScore: testScores?.greTotalScore || null,
                greVerbalScore: testScores?.greVerbalScore || null,
                greQuantitativeScore: testScores?.greQuantitativeScore || null,
                greWritingScore: testScores?.greWritingScore || null,
                tookActSatTests: testScores?.tookActSatTests ?? null,
                remembersTakingActSatTests:
                    testScores?.remembersTakingActSatTests ?? null,
            })
    }, [testScores, edit, setFormValues])

    const [update] = useMutation(UpdateCandidateTestScores)

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            let variables = { candidateId, ...formValues }
            if (formValues.tookActSatTests === false) {
                variables.remembersTakingActSatTests = true
            }
            if (formValues.remembersTakingActSatTests === false) {
                variables.tookActSatTests = true
            }
            await update({
                variables,
            })
            toast({
                title: 'Success',
                message: 'Test scores have been saved',
                severity: 'success',
            })
            closeAfterSave()
        } catch (e) {
            toast({
                title: 'Error',
                message: 'Failed to save test scores',
                severity: 'error',
            })
        }
    }

    const unsavedChangesExist = useMemo(() => {
        if (formValues.satMathScore !== testScores?.satMathScore) return true
        if (formValues.satVerbalScore !== testScores?.satVerbalScore)
            return true
        if (formValues.satWritingScore !== testScores?.satWritingScore)
            return true
        if (formValues.satTotal !== testScores?.satTotal) return true
        if (formValues.actScore !== testScores?.actScore) return true
        if (formValues.gmatScore !== testScores?.gmatScore) return true
        if (formValues.greTotalScore !== testScores?.greTotalScore) return true
        if (formValues.greVerbalScore !== testScores?.greVerbalScore)
            return true
        if (
            formValues.greQuantitativeScore !== testScores?.greQuantitativeScore
        )
            return true
        if (formValues.greWritingScore !== testScores?.greWritingScore)
            return true
        if (formValues.tookActSatTests !== testScores?.tookActSatTests)
            return true
        if (
            formValues.remembersTakingActSatTests !==
            testScores?.remembersTakingActSatTests
        )
            return true
        return false
    }, [formValues, testScores])

    useEffect(() => {
        if (unsavedChangesExist && edit === EDUCATION_SECTIONS.TEST_SCORES) {
            setUnsavedChangesExist(true)
        }
    }, [unsavedChangesExist, edit, setUnsavedChangesExist])

    return (
        <form onSubmit={handleSubmit}>
            <EducationSectionTitleContainer
                setEditWithUnsavedChanges={setEditWithUnsavedChanges}
                edit={edit}
                sectionType={EDUCATION_SECTIONS.TEST_SCORES}
                title={'Test Scores'}
                titleId="testScoresLabel"
            />
            {edit === EDUCATION_SECTIONS.TEST_SCORES ? (
                <>
                    <Grid
                        container
                        rowSpacing={getPxAsRem(32)}
                        columnSpacing={getPxAsRem(24)}
                    >
                        {/* SAT */}
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                inputProps={{
                                    type: 'number',
                                    min: 200,
                                    max: 800,
                                    step: 10,
                                }}
                                InputLabelProps={{ shrink: true }}
                                size="medium"
                                label="SAT Verbal"
                                name="satVerbalScore"
                                value={
                                    formValues.satVerbalScore
                                        ? formValues.satVerbalScore.toString()
                                        : ''
                                }
                                error={errorValues.satVerbalScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        satVerbalScore: +e.target.value,
                                    }))
                                    if (
                                        !e.target.value ||
                                        isValidSatScore(+e.target.value)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            satVerbalScore: '',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            satVerbalScore:
                                                'Please enter a valid SAT score between 200 and 800',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                type="number"
                                fullWidth
                                inputProps={{
                                    type: 'number',
                                    min: 200,
                                    max: 800,
                                    step: 10,
                                }}
                                InputLabelProps={{ shrink: true }}
                                size="medium"
                                label="SAT Writing"
                                name="satWritingScore"
                                value={
                                    formValues.satWritingScore
                                        ? formValues.satWritingScore.toString()
                                        : ''
                                }
                                error={errorValues.satWritingScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        satWritingScore: +e.target.value,
                                    }))

                                    if (
                                        !e.target.value ||
                                        isValidSatScore(+e.target.value)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            satWritingScore: '',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            satWritingScore:
                                                'Please enter a valid SAT score between 200 and 800',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                inputProps={{
                                    type: 'number',
                                    min: 200,
                                    max: 800,
                                    step: 10,
                                }}
                                InputLabelProps={{ shrink: true }}
                                size="medium"
                                label="SAT Math"
                                name="satMathScore"
                                value={
                                    formValues.satMathScore
                                        ? formValues.satMathScore.toString()
                                        : ''
                                }
                                error={errorValues.satMathScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        satMathScore: +e.target.value,
                                    }))
                                    if (
                                        !e.target.value ||
                                        isValidSatScore(+e.target.value)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            satMathScore: '',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            satMathScore:
                                                'Please enter a valid SAT score between 200 and 800',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    type: 'number',
                                    min: 600,
                                    max: 2400,
                                    step: 10,
                                }}
                                size="medium"
                                label="SAT Total"
                                name="satTotal"
                                value={
                                    formValues.satTotal
                                        ? formValues.satTotal?.toString()
                                        : ''
                                }
                                error={errorValues.satTotal !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        satTotal: +e.target.value,
                                    }))
                                    if (
                                        e.target.value &&
                                        (+e.target.value < 600 ||
                                            +e.target.value > 2400)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            satTotal:
                                                'Please enter a valid SAT score between 600 and 2400',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            satTotal: '',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        {errorValues.satMathScore ||
                        errorValues.satVerbalScore ||
                        errorValues.satWritingScore ||
                        errorValues.satTotal ? (
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    paddingTop: `${getPxAsRem(4)} !important`,
                                }}
                            >
                                <FormHelperText error>
                                    Please enter valid SAT scores
                                </FormHelperText>
                            </Grid>
                        ) : (
                            <></>
                        )}
                        {/* ACT */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    type: 'number',
                                    min: 1,
                                    max: 36,
                                    step: 1,
                                }}
                                size="medium"
                                label="ACT"
                                name="actScore"
                                value={
                                    formValues.actScore
                                        ? formValues.actScore?.toString()
                                        : ''
                                }
                                helperText={errorValues.actScore}
                                error={errorValues.actScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        actScore: +e.target.value,
                                    }))

                                    if (
                                        e.target.value &&
                                        (+e.target.value < 1 ||
                                            +e.target.value > 36)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            actScore:
                                                'Please enter a valid ACT score between 1 and 36',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            actScore: '',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sx={{ paddingTop: `${getPxAsRem(16)} !important` }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={(e, checked) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                tookActSatTests: !checked,
                                            }))
                                        }}
                                        checked={
                                            formValues.tookActSatTests === false
                                        }
                                    />
                                }
                                label="I did not take the SAT/ACT"
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sx={{ paddingTop: `${getPxAsRem(16)} !important` }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={(e, checked) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                remembersTakingActSatTests:
                                                    !checked,
                                            }))
                                        }}
                                        checked={
                                            formValues.remembersTakingActSatTests ===
                                            false
                                        }
                                    />
                                }
                                label="I do not remember my ACT/SAT score"
                            />
                        </Grid>
                        {/* GRE */}
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                inputProps={{
                                    type: 'number',
                                    min: 130,
                                    max: 170,
                                    step: 1,
                                }}
                                InputLabelProps={{ shrink: true }}
                                size="medium"
                                label="GRE Quantitative"
                                name="greQuantitativeScore"
                                value={
                                    formValues.greQuantitativeScore
                                        ? formValues.greQuantitativeScore.toString()
                                        : ''
                                }
                                error={errorValues.greQuantitativeScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        greQuantitativeScore: +e.target.value,
                                    }))
                                    if (
                                        !e.target.value ||
                                        isValidGreScore(+e.target.value)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            greQuantitativeScore: '',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            greQuantitativeScore:
                                                'Please enter a valid GRE score between 130 and 170',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                inputProps={{
                                    type: 'number',
                                    min: 130,
                                    max: 170,
                                    step: 1,
                                }}
                                InputLabelProps={{ shrink: true }}
                                size="medium"
                                label="GRE Verbal"
                                name="greVerbalScore"
                                value={
                                    formValues.greVerbalScore
                                        ? formValues.greVerbalScore.toString()
                                        : ''
                                }
                                error={errorValues.greVerbalScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        greVerbalScore: +e.target.value,
                                    }))
                                    if (
                                        !e.target.value ||
                                        isValidGreScore(+e.target.value)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            greVerbalScore: '',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            greVerbalScore:
                                                'Please enter a valid GRE score between 130 and 170',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                type="number"
                                fullWidth
                                inputProps={{
                                    type: 'number',
                                    min: 0,
                                    max: 6,
                                    step: 0.5,
                                }}
                                InputLabelProps={{ shrink: true }}
                                size="medium"
                                label="GRE Writing"
                                name="greWritingScore"
                                value={
                                    formValues.greWritingScore
                                        ? formValues.greWritingScore.toString()
                                        : ''
                                }
                                error={errorValues.greWritingScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        greWritingScore: +e.target.value,
                                    }))

                                    if (
                                        !e.target.value ||
                                        isValidGreScore(+e.target.value, true)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            greWritingScore: '',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            greWritingScore:
                                                'Please enter a valid GRE score between 0 and 6',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="number"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    type: 'number',
                                    min: 260,
                                    max: 340,
                                    step: 1,
                                }}
                                size="medium"
                                label="GRE Total"
                                name="greTotalScore"
                                value={
                                    formValues.greTotalScore
                                        ? formValues.greTotalScore?.toString()
                                        : ''
                                }
                                error={errorValues.greTotalScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        greTotalScore: +e.target.value,
                                    }))
                                    if (
                                        e.target.value &&
                                        (+e.target.value < 260 ||
                                            +e.target.value > 340 ||
                                            +e.target.value % 0.5 !== 0)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            greTotalScore:
                                                'Please enter a valid gre score between 260 and 340',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            greTotalScore: '',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                        {errorValues.greQuantitativeScore ||
                        errorValues.greVerbalScore ||
                        errorValues.greWritingScore ||
                        errorValues.greTotalScore ? (
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    paddingTop: `${getPxAsRem(4)} !important`,
                                }}
                            >
                                <FormHelperText error>
                                    Please enter valid GRE scores
                                </FormHelperText>
                            </Grid>
                        ) : (
                            <></>
                        )}
                        {/* GMAT */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    type: 'number',
                                    min: 205,
                                    max: 805,
                                    step: 5,
                                }}
                                size="medium"
                                label="GMAT"
                                name="gmatScore"
                                value={
                                    formValues.gmatScore
                                        ? formValues.gmatScore?.toString()
                                        : ''
                                }
                                helperText={errorValues.gmatScore}
                                error={errorValues.gmatScore !== ''}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        gmatScore: +e.target.value,
                                    }))
                                    if (
                                        +e.target.value >= 205 &&
                                        +e.target.value <= 805
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            gmatScore: '',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            gmatScore:
                                                'Please enter a valid GMAT score between 205 and 805',
                                        }))
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                    <EditButtonsContainer
                        activeEditingSection={edit}
                        sectionType={EDUCATION_SECTIONS.TEST_SCORES}
                        setEdit={setEdit}
                        setUnsavedChangesExist={setUnsavedChangesExist}
                        unsavedChangesExist={unsavedChangesExist}
                        onCancel={() => {
                            setFormValues({
                                satMathScore: testScores?.satMathScore || null,
                                satVerbalScore:
                                    testScores?.satVerbalScore || null,
                                satWritingScore:
                                    testScores?.satWritingScore || null,
                                satTotal: testScores?.satTotal || null,
                                actScore: testScores?.actScore || null,
                                gmatScore: testScores?.gmatScore || null,
                                greTotalScore:
                                    testScores?.greTotalScore || null,
                                greVerbalScore:
                                    testScores?.greVerbalScore || null,
                                greQuantitativeScore:
                                    testScores?.greQuantitativeScore || null,
                                greWritingScore:
                                    testScores?.greWritingScore || null,
                                tookActSatTests:
                                    testScores?.tookActSatTests || null,
                                remembersTakingActSatTests:
                                    testScores?.remembersTakingActSatTests ||
                                    null,
                            })
                        }}
                    />
                </>
            ) : (
                <>
                    <Box sx={{ display: 'flex' }}>
                        <Stack
                            sx={{
                                flexGrow: 1,
                                borderRight: `1px solid ${theme.palette.other.focus_dark}`,
                                marginRight: getPxAsRem(32),
                            }}
                        >
                            <Typography variant="body2">GMAT</Typography>
                            <Typography
                                variant="body2"
                                color={theme.palette.other.secondary_light}
                            >
                                {formValues.gmatScore || '-'}
                            </Typography>
                        </Stack>
                        <Stack
                            sx={{
                                flexGrow: 1,
                                borderRight: `1px solid ${theme.palette.other.focus_dark}`,
                                marginRight: getPxAsRem(32),
                            }}
                        >
                            <Typography variant="body2">SAT</Typography>
                            {!formValues.satMathScore &&
                                !formValues.satVerbalScore &&
                                !formValues.satWritingScore &&
                                !formValues.satTotal && (
                                    <Typography
                                        variant="body2"
                                        color={
                                            theme.palette.other.secondary_light
                                        }
                                    >
                                        -
                                    </Typography>
                                )}
                            {formValues.satMathScore && (
                                <Typography
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {formValues.satMathScore + ' Math'}
                                </Typography>
                            )}
                            {formValues.satVerbalScore && (
                                <Typography
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {formValues.satVerbalScore + ' Verbal'}
                                </Typography>
                            )}
                            {formValues.satWritingScore && (
                                <Typography
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {formValues.satWritingScore + ' Writing'}
                                </Typography>
                            )}
                            {formValues.satTotal && (
                                <Typography
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {formValues.satTotal + ' Total'}
                                </Typography>
                            )}
                        </Stack>
                        <Stack
                            sx={{
                                flexGrow: 1,
                                borderRight: `1px solid ${theme.palette.other.focus_dark}`,
                                marginRight: getPxAsRem(32),
                            }}
                        >
                            <Typography variant="body2">GRE</Typography>
                            {!formValues.greWritingScore &&
                                !formValues.greQuantitativeScore &&
                                !formValues.greVerbalScore &&
                                !formValues.greTotalScore && (
                                    <Typography
                                        variant="body2"
                                        color={
                                            theme.palette.other.secondary_light
                                        }
                                    >
                                        -
                                    </Typography>
                                )}
                            {formValues.greQuantitativeScore && (
                                <Typography
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {formValues.greQuantitativeScore +
                                        ' Quantitative'}
                                </Typography>
                            )}
                            {formValues.greVerbalScore && (
                                <Typography
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {formValues.greVerbalScore + ' Verbal'}
                                </Typography>
                            )}
                            {formValues.greWritingScore && (
                                <Typography
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {formValues.greWritingScore + ' Writing'}
                                </Typography>
                            )}
                            {formValues.greTotalScore && (
                                <Typography
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {formValues.greTotalScore + ' Total'}
                                </Typography>
                            )}
                        </Stack>
                        <Stack sx={{ flexGrow: 1 }}>
                            <Typography variant="body2">ACT</Typography>
                            <Typography
                                variant="body2"
                                color={theme.palette.other.secondary_light}
                            >
                                {formValues.actScore || '-'}
                            </Typography>
                        </Stack>
                    </Box>
                    {formValues.tookActSatTests === false && (
                        <Typography
                            variant="body2"
                            color={theme.palette.other.secondary_light}
                            sx={{ marginTop: getPxAsRem(8) }}
                        >
                            I did not take the SAT/ACT
                        </Typography>
                    )}
                    {formValues.remembersTakingActSatTests === false && (
                        <Typography
                            variant="body2"
                            color={theme.palette.other.secondary_light}
                            sx={{ marginTop: getPxAsRem(8) }}
                        >
                            I do not remember my ACT/SAT score
                        </Typography>
                    )}
                </>
            )}
        </form>
    )
}

export default TestScores
